import { useCallback, useEffect, useState } from "react";
import type { Patient, WaitlistEntry, WaitlistPartOfDay } from "@dentacore/shared-types";
import {
  WAITLIST_PART_OF_DAY_LABELS,
  WAITLIST_PARTS_OF_DAY,
  WEEKDAY_LABELS,
} from "@dentacore/shared-types";

import { searchPatients } from "../api/patients";
import {
  addToWaitlist,
  cancelWaitlistEntry,
  listWaitlist,
  updateWaitlistEntry,
} from "../api/waitlist";
import { Pager, useFitPages, useRoomBelow } from "../ui/paging";
import { WAITLIST_DRAG_TYPE } from "./grid-geometry";
import styles from "./waitlist-strip.module.css";
import { RequiredMark } from "../ui/required-mark";

/** The compact pager and the card's bottom padding, under the rows. */
const PAGER_ROOM = 40;

/**
 * How a waiting patient's availability reads on one line.
 *
 * **Says "Any day" rather than nothing** when no weekday is ticked. An empty
 * string there would read as missing information, when it is in fact the most
 * useful state an entry can be in — somebody who will take anything.
 */
function availabilityOf(entry: WaitlistEntry): string {
  const days =
    entry.preferredWeekdays.length === 0
      ? "Any day"
      : WEEKDAY_LABELS.filter((day) => entry.preferredWeekdays.includes(day.value))
          .map((day) => day.short)
          .join(", ");

  const when =
    entry.preferredPartOfDay === "ANY"
      ? ""
      : ` · ${WAITLIST_PART_OF_DAY_LABELS[entry.preferredPartOfDay]}`;

  const from = entry.waitingFrom === null ? "" : ` · from ${entry.waitingFrom}`;
  const until = entry.waitingUntil === null ? "" : ` · until ${entry.waitingUntil}`;

  return `${days}${when}${from}${until}`;
}

/**
 * The queue itself — who is waiting, and how somebody joins it (STORY-038).
 *
 * ── Why this exists at all ──────────────────────────────────────────────────
 *
 * The story's acceptance check is about *surfacing* and *filling* a queue, and
 * the detail panel does both. But a queue nobody can join is not a feature:
 * without this strip the only way onto the waitlist would be a direct API call,
 * which is not a product. So the smallest honest thing is here — see who is
 * waiting, put somebody on, take somebody off.
 *
 * ── The mockup draws a waitlist strip on Home, not on the calendar ──────────
 *
 * Working plan §3.7 calls it "the Home waitlist strip", and Home is Phase 8.
 * The calendar is where a slot comes free and where somebody would think to add
 * a patient who wants an earlier one, so it lives here until Home exists. Same
 * component, different page — nothing here knows which screen it is on.
 *
 * ── Free text, on purpose ───────────────────────────────────────────────────
 *
 * `preferredWindow` is *"mornings"*, *"any Tuesday"*, *"after 4pm"*. It is
 * never matched on — the server matches on duration and asks the conflict
 * service — so this is a plain text field rather than a picker, and the schema
 * says why: an enum here would be wrong within a week.
 */
export function WaitlistStrip({
  branchId,
  version,
  onChanged,
  onDragEntry,
  onCount,
}: {
  branchId: string | null;
  /**
   * Bumped by the page when something outside this component may have changed
   * the queue — chiefly the detail panel taking somebody off it to fill a freed
   * slot. Without it the strip keeps showing a patient who has just been booked,
   * because nothing it owns changed.
   */
  version: number;
  /** A change may alter who could fill a freed slot the panel is showing. */
  onChanged: () => void;
  /**
   * Which entry is being dragged onto the calendar, and null when the drag ends
   * (Anthony's QA, item 14).
   *
   * **The entry travels through React, not through `dataTransfer`.** Only the
   * id goes into the drag payload, because a payload is readable by whatever
   * application the drag is dropped into and a patient's name has no business
   * being there. The page holding both this and the grid resolves the two.
   */
  onDragEntry?: (entry: WaitlistEntry | null) => void;
  /**
   * How many are waiting, for the calendar header's counter (CS6). Reported
   * only while no filter is applied — the counter counts the queue, and a
   * filtered list is an answer to a narrower question.
   */
  onCount?: (count: number) => void;
}): React.JSX.Element | null {
  const [entries, setEntries] = useState<WaitlistEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<Patient[]>([]);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [minutes, setMinutes] = useState("30");
  const [window, setWindow] = useState("");
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [partOfDay, setPartOfDay] = useState<WaitlistPartOfDay>("ANY");
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");

  /**
   * Which entry is being edited, or null when adding a new one.
   *
   * **The same form does both** (Anthony's QA, item 13). Two forms asking for
   * the same six fields is two forms to keep in step, and the second one is
   * always the one that misses a field when a field is added.
   */
  const [editingId, setEditingId] = useState<string | null>(null);

  /**
   * Narrowing the queue to who could take a given slot (Anthony's QA, #13).
   *
   * *"if we have many waitlisted, i think the system should filter by day of
   * the week and timing."* Held separately from the add form's own fields —
   * they look alike and mean opposite things: one records what a patient wants,
   * the other describes a slot being filled.
   */
  const [filterWeekday, setFilterWeekday] = useState("");
  const [filterPartOfDay, setFilterPartOfDay] = useState<WaitlistPartOfDay>("ANY");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filtering, setFiltering] = useState(false);

  const reload = useCallback(
    (signal?: AbortSignal): Promise<void> =>
      listWaitlist(
        {
          status: "WAITING",
          ...(filterWeekday === "" ? {} : { weekday: Number(filterWeekday) }),
          ...(filterPartOfDay === "ANY" ? {} : { partOfDay: filterPartOfDay }),
          ...(filterFrom === "" ? {} : { from: filterFrom }),
          ...(filterTo === "" ? {} : { to: filterTo }),
        },
        signal,
      )
        .then(setEntries)
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          setEntries([]);
          setError(cause instanceof Error ? cause.message : "Could not read the waitlist.");
        }),
    [filterWeekday, filterPartOfDay, filterFrom, filterTo],
  );

  useEffect(() => {
    if (branchId === null) return;
    const controller = new AbortController();

    setEntries(null);
    void reload(controller.signal);

    return () => controller.abort();
  }, [branchId, version, reload]);

  const unfiltered =
    filterWeekday === "" && filterPartOfDay === "ANY" && filterFrom === "" && filterTo === "";
  useEffect(() => {
    if (entries !== null && unfiltered) onCount?.(entries.length);
  }, [entries, unfiltered, onCount]);

  /** Patient search, debounced, and only once there is enough to search on. */
  useEffect(() => {
    if (query.trim().length < 2) {
      setMatches([]);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchPatients({ q: query, limit: 5 }, controller.signal)
        .then((page) => setMatches(page.patients))
        .catch(() => setMatches([]));
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  /**
   * **Pages, never a scroll** (Emile, 2026-10-17: *"please always calculate to
   * fit screen size"*). The strip sits in the calendar's side panel, which grows
   * with what it holds, so a page is however many whole rows fit between the
   * list's top and the bottom of the content area — recounted as the window, or
   * a form opening above the list, changes it.
   */
  const [roomRef, room] = useRoomBelow(PAGER_ROOM);
  const { measure, page, setPage, pages, range } = useFitPages({
    keys: (entries ?? []).map((entry) => entry.id),
    estimate: () => 66,
    gap: 0,
    resetOn: `${String(branchId)}|${filterWeekday}|${filterPartOfDay}|${filterFrom}|${filterTo}`,
    budget: room,
  });

  if (branchId === null) return null;

  /**
   * Add somebody, or save the edit — **one path, because it is one form**.
   *
   * Editing keeps the entry's place in the queue (Anthony's QA, item 13): the
   * queue is worked oldest-first, and correcting a duration used to mean
   * cancelling and re-adding, which sent the patient to the back of it.
   */
  const save = (): void => {
    if (editingId === null && patient === null) return;

    setBusy(true);
    setError(null);

    const availability = {
      durationMinutes: Number(minutes),
      // Sent even when empty, so clearing a preference clears it. Omitting the
      // field on a PATCH means "leave it alone", which would make a tick
      // impossible to untick.
      preferredWeekdays: weekdays,
      preferredPartOfDay: partOfDay,
      ...(window.trim() === "" ? {} : { preferredWindow: window.trim() }),
      ...(from === "" ? {} : { waitingFrom: from }),
      ...(until === "" ? {} : { waitingUntil: until }),
    };

    const written =
      editingId === null
        ? addToWaitlist({ patientId: (patient as Patient).id, ...availability })
        : updateWaitlistEntry(editingId, availability);

    written
      .then(() => reload())
      .then(() => {
        clear();
        setAdding(false);
        setEditingId(null);
        onChanged();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "That did not save.");
      })
      .finally(() => setBusy(false));
  };

  /** Open the form on an existing entry, with its own values in it. */
  const edit = (entry: WaitlistEntry): void => {
    setEditingId(entry.id);
    setAdding(true);
    setError(null);
    setPatient(null);
    setQuery("");
    setMinutes(String(entry.durationMinutes));
    setWindow(entry.preferredWindow ?? "");
    setWeekdays(entry.preferredWeekdays);
    setPartOfDay(entry.preferredPartOfDay);
    setFrom(entry.waitingFrom ?? "");
    setUntil(entry.waitingUntil ?? "");
  };

  const remove = (entryId: string): void => {
    setBusy(true);
    setError(null);

    cancelWaitlistEntry(entryId)
      .then(() => reload())
      .then(() => onChanged())
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "That did not save.");
      })
      .finally(() => setBusy(false));
  };

  const canSave = (editingId !== null || patient !== null) && Number(minutes) > 0 && !busy;

  const filtersApplied =
    filterWeekday !== "" || filterPartOfDay !== "ANY" || filterFrom !== "" || filterTo !== "";

  /**
   * **Nothing survives the form closing** (client's manual QA, item 17).
   *
   * Closing and reopening used to show whatever was half-typed last time, and
   * so did adding somebody successfully — so the next patient's entry began
   * pre-filled with the previous one's minutes and window. The client asked for
   * both: "it should be wiped in case I did not add the patient, and after
   * adding him."
   */
  const clear = (): void => {
    setQuery("");
    setMatches([]);
    setPatient(null);
    setMinutes("30");
    setWindow("");
    setWeekdays([]);
    setPartOfDay("ANY");
    setFrom("");
    setUntil("");
    setEditingId(null);
    setError(null);
  };

  return (
    <section className={styles.strip} data-testid="waitlist-strip">
      <div className={styles.head}>
        <span className={styles.label}>Waiting for an earlier slot</span>
        {/*
          **The number alone.** "0 waiting" wrapped to two lines in a 248px
          header beside a title that already says what is being counted, and the
          body below says "Nobody is waiting" in words anyway.
        */}
        <span className={styles.count} title="Patients waiting for an earlier slot">
          {entries === null ? "…" : String(entries.length)}
        </span>
        {/*
          **Filtering is its own toggle, beside adding** (Anthony's QA, #13).
          A queue of three needs neither; a queue of forty needs the filters and
          would be worse for having them permanently open in a 248px column.
        */}
        <button
          type="button"
          className={styles.toggle}
          aria-pressed={filtering}
          onClick={() => setFiltering((open) => !open)}
          data-testid="waitlist-filter-toggle"
        >
          {filtersApplied ? "Filtered" : "Filter"}
        </button>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => {
            setAdding((open) => {
              if (open) clear();
              return !open;
            });
          }}
          data-testid="waitlist-add-toggle"
        >
          {adding ? "Close" : "Add someone"}
        </button>
      </div>

      {filtering && (
        <div className={styles.filters} data-testid="waitlist-filters">
          <p className={styles.hint}>
            Who could take a slot of this shape. Anyone with no preference of their own is always
            included &mdash; they are the most likely to say yes.
          </p>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Day of the week</span>
            <select
              className={styles.input}
              value={filterWeekday}
              onChange={(event) => setFilterWeekday(event.target.value)}
              data-testid="waitlist-filter-weekday"
            >
              <option value="">Any day</option>
              {WEEKDAY_LABELS.map((day) => (
                <option key={day.value} value={String(day.value)}>
                  {day.long}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Time of day</span>
            <select
              className={styles.input}
              value={filterPartOfDay}
              onChange={(event) => setFilterPartOfDay(event.target.value as WaitlistPartOfDay)}
              data-testid="waitlist-filter-part-of-day"
            >
              {WAITLIST_PARTS_OF_DAY.map((value) => (
                <option key={value} value={value}>
                  {WAITLIST_PART_OF_DAY_LABELS[value]}
                </option>
              ))}
            </select>
          </label>

          <div className={styles.range}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Between</span>
              <input
                className={styles.input}
                type="date"
                value={filterFrom}
                onChange={(event) => setFilterFrom(event.target.value)}
                data-testid="waitlist-filter-from"
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>and</span>
              <input
                className={styles.input}
                type="date"
                value={filterTo}
                onChange={(event) => setFilterTo(event.target.value)}
                data-testid="waitlist-filter-to"
              />
            </label>
          </div>

          {filtersApplied && (
            <button
              type="button"
              className={styles.toggle}
              onClick={() => {
                setFilterWeekday("");
                setFilterPartOfDay("ANY");
                setFilterFrom("");
                setFilterTo("");
              }}
              data-testid="waitlist-filter-clear"
            >
              Show everybody
            </button>
          )}
        </div>
      )}

      {error !== null && (
        <p className={styles.error} data-testid="waitlist-strip-error">
          {error}
        </p>
      )}

      {adding && (
        <div className={styles.form} data-testid="waitlist-form">
          {/*
            **Hidden while editing** — an entry is *this* person waiting, and
            pointing it at somebody else would be a new entry wearing an old
            one's seniority. The server refuses it too: `UpdateWaitlistEntry`
            omits `patientId` deliberately.
          */}
          <label className={styles.field} hidden={editingId !== null}>
            <span className={styles.fieldLabel}>
              Patient
              <RequiredMark />
            </span>
            {patient === null ? (
              <input
                className={styles.input}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Name, phone or file number"
                aria-label="Find a patient for the waitlist"
                data-testid="waitlist-patient-search"
              />
            ) : (
              <button
                type="button"
                className={styles.chosen}
                onClick={() => setPatient(null)}
                data-testid="waitlist-patient-chosen"
              >
                {patient.firstName} {patient.lastName} · change
              </button>
            )}
          </label>

          {matches.length > 0 && patient === null && editingId === null && (
            <div className={styles.matches} data-testid="waitlist-patient-matches">
              {matches.map((match) => (
                <button
                  key={match.id}
                  type="button"
                  className={styles.match}
                  onClick={() => {
                    setPatient(match);
                    setMatches([]);
                  }}
                  data-card=""
                  data-testid="waitlist-patient-match"
                >
                  {match.firstName} {match.lastName} · {match.fileNumberDisplay}
                </button>
              ))}
            </div>
          )}

          {/*
            **"Appointment length", not "Minutes"** (Anthony's QA, item 13).

            His words: *"the minutes what do they really mean (manna mafhoume
            eno ade bedo waet, maybe needed time?)"* — he guessed right, from a
            label that gave him nothing to guess from. On a card about waiting,
            a bare "Minutes" reads as *how long they have been waiting*, which
            is the opposite of what it records.
          */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Appointment length
              <RequiredMark />
            </span>
            <div className={styles.withSuffix}>
              <input
                className={styles.narrow}
                value={minutes}
                inputMode="numeric"
                onChange={(event) => setMinutes(event.target.value)}
                aria-label="How long the appointment they are waiting for needs"
                data-testid="waitlist-minutes"
              />
              <span className={styles.suffix}>minutes of chair time</span>
            </div>
          </label>

          {/*
            **The machine-readable half of "when suits you"** (Anthony's QA,
            item 13). Ticking nothing means any day, which is the common answer
            and the one that matches every search — so the form does not insist.
          */}
          <fieldset className={styles.days}>
            <legend className={styles.fieldLabel}>Days they can come</legend>
            {WEEKDAY_LABELS.map((day) => (
              <label key={day.value} className={styles.day}>
                <input
                  type="checkbox"
                  checked={weekdays.includes(day.value)}
                  onChange={(event) => {
                    setWeekdays((current) =>
                      event.target.checked
                        ? [...current, day.value].sort((a, b) => a - b)
                        : current.filter((value) => value !== day.value),
                    );
                  }}
                  aria-label={day.long}
                  data-testid={`waitlist-weekday-${String(day.value)}`}
                />
                <span>{day.short}</span>
              </label>
            ))}
            <span className={styles.hint}>
              {weekdays.length === 0 ? "Any day" : "Only the days ticked"}
            </span>
          </fieldset>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Time of day</span>
            <select
              className={styles.input}
              value={partOfDay}
              onChange={(event) => setPartOfDay(event.target.value as WaitlistPartOfDay)}
              data-testid="waitlist-part-of-day"
            >
              {WAITLIST_PARTS_OF_DAY.map((value) => (
                <option key={value} value={value}>
                  {WAITLIST_PART_OF_DAY_LABELS[value]}
                </option>
              ))}
            </select>
          </label>

          <div className={styles.range}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Not before</span>
              <input
                className={styles.input}
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                data-testid="waitlist-from"
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Not after</span>
              <input
                className={styles.input}
                type="date"
                value={until}
                onChange={(event) => setUntil(event.target.value)}
                data-testid="waitlist-until"
              />
            </label>
          </div>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Anything else</span>
            {/*
              Still free text and still never parsed. The ticks above carry what
              a machine can filter on; this carries what it never could — "any
              Tuesday after the 20th, but not the week of the wedding".
            */}
            <input
              className={styles.input}
              value={window}
              onChange={(event) => setWindow(event.target.value)}
              placeholder="Anything the ticks above cannot say"
              aria-label="Anything else about when they can come"
              data-testid="waitlist-window"
            />
          </label>

          <button
            type="button"
            className={styles.primary}
            disabled={!canSave}
            onClick={save}
            data-testid="waitlist-save"
          >
            {editingId === null ? "Add to waitlist" : "Save changes"}
          </button>

          {/*
            **Why the button is off, next to the button** (client's manual QA,
            item 16). It is disabled until a patient is chosen, and the list to
            choose from only appears once you have typed — so somebody who typed
            a name and looked for a way to submit found an inert control and no
            reason for it. The client's words were "I have no way to click on
            add to waitlist".
          */}
          {patient === null && editingId === null && (
            <span className={styles.needsPatient} data-testid="waitlist-needs-patient">
              Find the patient above and choose them from the list.
            </span>
          )}

          {editingId !== null && (
            <span className={styles.needsPatient} data-testid="waitlist-editing">
              Editing keeps their place in the queue.
            </span>
          )}
        </div>
      )}

      {/*
        **One line, not four.** The long form explained the whole feature in a
        248px column, which is four lines of muted text saying what the card's
        own title and its one button already imply. What is left is the fact a
        reader needs: there is nobody, and what adding one does.
      */}
      {entries !== null && entries.length === 0 && !adding && (
        <p className={styles.empty} data-testid="waitlist-strip-empty">
          Nobody is waiting. Anyone added here is offered a cancelled slot.
        </p>
      )}

      {entries !== null && entries.length > 0 && (
        <div
          className={styles.rows}
          ref={roomRef}
          style={room > 0 ? { minHeight: room } : undefined}
        >
          {entries.slice(range[0], range[1]).map((entry) => (
            /*
              **Draggable onto a chair column** — Anthony's QA, item 14: *"let
              me drag the waitlist to the calendar"*.

              The point of the request is not the gesture, it is that filling a
              gap should not require a cancellation to have just happened: *"lets
              assume more then 1 canceled and i have the opportunity to squeeze
              in like i want"*. The matches strip only ever appears against one
              cancelled booking, so choosing a gap yourself had no route at all.

              **Native drag-and-drop, not the grid's pointer events.** Those
              belong to rescheduling, which is the one interaction where a bug
              moves a real appointment; the browser arbitrates between the two
              channels so they cannot be in progress at once.

              Only the id travels — see `WAITLIST_DRAG_TYPE`. A patient's name
              in a drag payload is readable by any page the drag crosses.
            */
            <div
              key={entry.id}
              ref={measure(entry.id)}
              className={styles.row}
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData(WAITLIST_DRAG_TYPE, entry.id);
                event.dataTransfer.effectAllowed = "copy";
                onDragEntry?.(entry);
              }}
              // Fires whether the drag was dropped or abandoned, so a drag let
              // go over nothing cannot leave a stale entry armed for the next.
              onDragEnd={() => onDragEntry?.(null)}
              data-testid="waitlist-row"
            >
              <span className={styles.who}>
                <span className={styles.name}>{entry.patientName}</span>
                <span className={styles.needs}>
                  {String(entry.durationMinutes)} min
                  {entry.preferredWindow === null ? "" : ` · ${entry.preferredWindow}`}
                </span>
                {/* The ticked availability, in words, so the row can be read
                    against a slot without opening it (Anthony's QA, item 13). */}
                <span className={styles.needs} data-testid="waitlist-availability">
                  {availabilityOf(entry)}
                </span>
              </span>
              <span className={styles.rowActions}>
                <button
                  type="button"
                  className={styles.remove}
                  disabled={busy}
                  onClick={() => edit(entry)}
                  data-testid="waitlist-edit"
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={styles.remove}
                  disabled={busy}
                  onClick={() => remove(entry.id)}
                  data-testid="waitlist-remove"
                >
                  Remove
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
      {entries !== null && (
        <Pager
          compact
          page={page}
          count={pages.length}
          onPage={setPage}
          from={range[0] + 1}
          to={range[1]}
          total={entries.length}
          noun="waiting"
          testId="waitlist-pager"
        />
      )}
    </section>
  );
}
